export type UserRegionalPrefs = {
  locale?: string | null;
  timezone?: string | null;
  dateFormat?: string | null;
};

export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const DATE_FORMAT_OPTIONS: Record<string, Intl.DateTimeFormatOptions> = {
  'MM/dd/yyyy': { month: '2-digit', day: '2-digit', year: 'numeric' },
  'dd/MM/yyyy': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'yyyy-MM-dd': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'dd.MM.yyyy': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'MMMM d, yyyy': { month: 'long', day: 'numeric', year: 'numeric' },
};

function resolveDateOptions(prefs?: UserRegionalPrefs): Intl.DateTimeFormatOptions {
  const key = prefs?.dateFormat || 'MM/dd/yyyy';
  return DATE_FORMAT_OPTIONS[key] || DATE_FORMAT_OPTIONS['MM/dd/yyyy'];
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '' || value === 'none') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatUserDate(
  value: string | Date | null | undefined,
  prefs?: UserRegionalPrefs,
): string {
  const d = toDate(value);
  if (!d) return value == null || value === '' ? '' : String(value);
  const locale = prefs?.locale || undefined;
  const timeZone = prefs?.timezone || undefined;
  return d.toLocaleDateString(locale, {
    ...resolveDateOptions(prefs),
    timeZone,
  });
}

export function formatUserDateTime(
  value: string | Date | null | undefined,
  prefs?: UserRegionalPrefs,
): string {
  const d = toDate(value);
  if (!d) return value == null || value === '' ? '' : String(value);
  const locale = prefs?.locale || undefined;
  const timeZone = prefs?.timezone || undefined;
  return d.toLocaleString(locale, {
    ...resolveDateOptions(prefs),
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
}

export const toLocalDateInput = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getTodayLocalDateInput = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a task due date for display (activity history, etc.).
 * Pass profile prefs when available (see useUserRegionalPrefs).
 */
export function formatDueDateForDisplay(
  value: string | null | undefined,
  prefs?: UserRegionalPrefs,
): string {
  if (value == null || value === '' || value === 'none') return 'none';
  const formatted = formatUserDate(value, prefs);
  return formatted || String(value);
}

/** Short date for tables and cards — e.g. Mar 5, 2026 */
export function formatUserDateShort(
  value: string | Date | null | undefined,
  prefs?: UserRegionalPrefs,
): string {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(prefs?.locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: prefs?.timezone || undefined,
  });
}
